import "../App.css";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../useTranslation";

type UsernamePasswordFormComponentProps = {
  setStep: (status: string) => void;
};

type Language = "ja" | "en";

const translations = {
  ja: {
    username: "ユーザー名: ",
    password: "パスワード: ",
    usernamePlaceholder: "ユーザー名",
    passwordPlaceholder: "パスワード",
    login: "ログイン",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "利用規約",
    privacyPolicy: "プライバシーポリシー",
    incorrectUsernameOrPassword: "ユーザー名またはパスワードが正しくありません。",
    internalError: "内部エラーが発生しました。",
    failedToLogin: "ログインに失敗しました。",
    invalidStatusCode: "無効なステータスコードです。",
    iicWebURL: "https://www.iic-hq.co.jp/",
    sitepolicyWebURL: "https://www.ihi.co.jp/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/privacy/",
  },
  en: {
    username: "Username: ",
    password: "Password: ",
    usernamePlaceholder: "Username",
    passwordPlaceholder: "Password",
    login: "Login",
    poweredBy: "Powered by IIC",
    iicHome: "IIC Home",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    incorrectUsernameOrPassword: "Incorrect username or password.",
    internalError: "An internal error has occurred.",
    failedToLogin: "Failed to login.",
    invalidStatusCode: "Invalid status code.",
    iicWebURL: "https://www.iic-hq.co.jp/en/",
    sitepolicyWebURL: "https://www.ihi.co.jp/en/sitepolicy/",
    privacyWebURL: "https://www.ihi.co.jp/en/privacy/",
  },
};

function getLanguageFromURL(): Language {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get("lang");
  return lang === "en" ? "en" : "ja";
}

// Translate server/client error messages based on lang
const translateError = (lang: "ja" | "en", msg: string) => {
  if (lang === "en") {
    const map: { [k: string]: string } = {
      "ユーザー名またはパスワードが正しくありません。":
        "Incorrect username or password.",
      "内部エラーが発生しました。": "An internal error has occurred.",
      "ログインに失敗しました。": "Failed to login.",
      "無効なステータスコードです。": "Invalid status code.",
      "データの取得中にエラーが発生しました。":
        "An error occurred while retrieving the data.",
      "認証されていません。": "Unauthorized.",
      "正しい認証情報で再度ログインしてください。":
        "Please try logging in again with correct credentials.",
    };
    if (map[msg]) return map[msg];
  }
  return msg;
};

function UsernamePasswordFormComponent({
  setStep,
}: UsernamePasswordFormComponentProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null); // Add this state variable to hold an error message
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [language, setLanguage] = useState<Language>(getLanguageFromURL());

  const apiUrl = process.env.REACT_APP_EXPRESS_API_URL
    ? process.env.REACT_APP_EXPRESS_API_URL
    : "http://localhost:3001";

  // Check language parameter on mount and when URL changes
  useEffect(() => {
    const checkLanguage = () => {
      setLanguage(getLanguageFromURL());
    };
    // Check immediately on mount/reload
    checkLanguage();
    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", checkLanguage);
    return () => {
      window.removeEventListener("popstate", checkLanguage);
    };
  }, []);

  const t = translations[language];

  useEffect(() => {
    const handleKeyDown = (e: { key: string }) => {
      if (e.key === "Enter" && buttonRef.current) {
        // Check if the button is visible
        if (window.getComputedStyle(buttonRef.current).display !== "none") {
          buttonRef.current.click();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  const handleSubmit = () => {
    if (isClicked) return; // Prevent multiple clicks. TODO: reset after X seconds
    var body = { username: username, password: password };

    setIsClicked(true); // Disable the button

    // hit server to get token
    axios
      .post(`${apiUrl}/login`, body)
      .then((res) => {
        // Ensure the response is valid before continuing
        if (res.status !== 200) {
          if (res.status === 401) {
            throw new Error(t.incorrectUsernameOrPassword);
          } else if (res.status === 500) {
            throw new Error(t.internalError);
          }
          throw new Error(t.failedToLogin);
        }

        // Define body for the second request and continue the chain
        return res.data;
      })
      .then((data) => {
        setError(null);

        sessionStorage.setItem("token", data.token);

        if (data.status === "send-code") {
          setStep("enter-verification-code");
        } else if (data.status === "need-to-persist-phone") {
          setStep("store-phone");
        } else if (data.status === "verified") {
          // This I believe is vulnerable to XSS (and was this way before I joined)
          // If account/site is 2fa enabled, skipping to this step should result in a token
          // that won't work
          var legacy_element = document.getElementById(
            "elementId"
          ) as HTMLInputElement;
          if (!legacy_element) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "token";
            input.id = "token";
            input.value = data.token;
            document.body.appendChild(input);
          } else {
            legacy_element.value = data.token;
          }
          setStep("verified");
        } else {
          console.log("how did this happen?");
          var msg = t.invalidStatusCode;
          setError(msg);
          setIsClicked(false);
        }
      })
      .catch((error) => {
        // Handle any errors that occurred in the above chain
        var msg = error.response.data.status
          ? error.response.data.status
          : error.message;
        setError(translateError(language, msg));
        setIsClicked(false);
      });
  };
  return (
    <div className="center">
      <img className="logo" src="images/Dap3_logo.png" alt="IIC's logo"></img>
      <form>
        <table align="center" style={{ width: "80%" }}>
          <tbody>
            <tr>
              <td className="dlabel" style={{fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif'}}>{t.username}</td>
              <td>
                <input
                  type="text"
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t.usernamePlaceholder}
                  className="input"
                  autoComplete="username"
                />
              </td>
            </tr>
            <tr>
              <td className="dlabel" style={{fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif'}}>{t.password}</td>
              <td>
                <input
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  className="input"
                  autoComplete="current-password"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </form>
      <br />
      <div
        className="dlabel"
        id="login_feedback"
        style={{ color: "#ff0000", display: "block", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}
      >
        &nbsp; {error} {/* Display the error message if there is one */}
      </div>
      <br />
      <button
        ref={buttonRef}
        onClick={handleSubmit}
        className="login_button"
        value={t.login}
        disabled={isClicked}
        style={{ backgroundColor: isClicked ? "gray" : "", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }} // Optional: change color to gray when clicked
      >
        {t.login}
      </button>
      <div
        className="dlabel"
        style={{ margin: "0 auto", display: "block", paddingTop: "10%" }}
      >
        {" "}
        {t.poweredBy}
      </div>
      <div
        className="dlabel"
        style={{
          fontWeight: "normal",
          padding: "3%",
          paddingTop: "2%",
          margin: "0 auto",
          display: "block",
        }}
      >
        <a href={t.iicWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.iicHome}
        </a>{" "}
        |{" "}
        <a href={t.sitepolicyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.termsOfUse}
        </a>{" "}
        |{" "}
        <a href={t.privacyWebURL} target="_blank" rel= "noopener noreferrer" style={{ color: "white", fontFamily:'"Meiryo UI", Meiryo, Arial, sans-serif' }}>
          {t.privacyPolicy}
        </a>
      </div>
    </div>
  );
}

export default UsernamePasswordFormComponent;
